<template>
  <div class="my-5">
    <guest-details-form
      v-if="booker"
      :guest="booker"
      :is-booker="true"
      :booker="booker"
      :extras="extras"
      :addons="addons"
      :addonExtras="addonExtras"
      :assigned-addons="assignedAddons"
      :assigned-extras="assignedExtras"
      :assigned-addon-extras="assignedAddonExtras"
      :available-addons="availableAddons"
      :available-extras="availableExtras"
      :available-addon-extras="availableAddonExtras"
      :passport-types="passportTypes"
      :start-open="true"
      :parent-form-errors="getGuestErrors(0)"
      :data-index="0"
      @update-guest="onUpdateGuest"
      @update-unfilled-fields="onUpdateUnfilledFields"
    ></guest-details-form>
    <guest-details-form
      v-for="(guest, index) in additionalGuests"
      :key="index"
      :guest="guest"
      :is-booker="false"
      :booker="booker"
      :extras="extras"
      :addons="addons"
      :guestNumber="index + 1"
      :addonExtras="addonExtras"
      :assigned-addons="assignedAddons"
      :assigned-extras="assignedExtras"
      :assigned-addon-extras="assignedAddonExtras"
      :available-addons="availableAddons"
      :available-extras="availableExtras"
      :available-addon-extras="availableAddonExtras"
      :passport-types="passportTypes"
      :parent-form-errors="getGuestErrors(index + 1)"
      :data-index="index + 1"
      @update-guest="onUpdateGuest"
      @update-unfilled-fields="onUpdateUnfilledFields"
    ></guest-details-form>
    <div class="w-full my-5 h-px border-b border-black-0.1"></div>
    <div v-if="errorMessage" class="text-red-400 my-4">
      {{ errorMessage }}
    </div>
    <button
      v-if="!allComplete"
      type="button"
      class="button accbook min-w-280 md:min-w-360"
      @click.prevent="onSubmit"
      :disabled="submitting"
    >
      {{ submitting ? 'Submitting...' : 'Update all' }}
    </button>
  </div>
</template>

<script>
import GuestDetailsForm from './GuestDetailsForm.vue';
import find from 'lodash/find';
import HandlesErrors from './Mixins/HandlesErrors';
import Route from '../src/Route';
import get from 'lodash/get';
import forOwn from 'lodash/forOwn';

export default {
  components: { GuestDetailsForm },

  mixins: [HandlesErrors],

  props: {
    booking: {
      type: Object,
      required: true,
    },
    passportTypes: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      guests: [],
      submitting: false,
      unfilledFields: [],
      addons: [],
      extras: [],
      assignedAddons: {},
      assignedExtras: {},
      assignedAddonExtras: {},
      availableAddons: {},
      availableExtras: {},
      availableAddonExtras: {},
      guestErrors: [],
    };
  },

  computed: {
    booker() {
      return this.guests.find((guest) => guest.booker);
    },

    additionalGuests() {
      return this.guests.filter((guest) => !guest.booker);
    },

    addonExtras() {
      const items = [];

      this.addons.forEach((addon) => {
        items.push(...addon.extras);
      });

      return items;
    },

    unlockedGuests() {
      return this.guests.filter((guest) => !guest.locked);
    },

    allComplete() {
      return this.unlockedGuests.length < 1;
    },
  },

  created() {
    this.getData();

    this.booking.data.addons.forEach((addon) => {
      this.availableAddons[addon.id] = addon.quantity;

      if (addon.extras) {
        addon.extras.forEach((addonExtra) => {
          this.availableAddonExtras[addonExtra.id] = addonExtra.quantity;
        });
      }
    });

    this.booking.data.extras.forEach((extra) => {
      this.availableExtras[extra.id] = extra.quantity;
    });
  },

  methods: {
    getData() {
      axios
        .get(Route('json.bookings.show', { booking: this.booking.uuid }))
        .then((response) => {
          if (response.status === 200) {
            this.guests = get(response, 'data.booking.guests');
            this.addons = get(response, 'data.booking.addons');
            this.extras = get(response, 'data.booking.extras');

            this.updateSelectedAdditions();
          } else {
            throw 'There was a problem.';
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },

    onUpdateGuest(updatedGuest) {
      let index = this.guests.findIndex((item) => item.uuid === updatedGuest.uuid);

      this.guests = [
        ...this.guests.slice(0, index),
        updatedGuest,
        ...this.guests.slice(index + 1, this.guests.length),
      ];

      this.updateSelectedAdditions();
    },

    updateSelectedAdditions() {
      const addons = {};
      const extras = {};
      const addonExtras = {};

      this.booking.data.addons.forEach((addon) => {
        addons[addon.id] = 0;

        this.guests.forEach((guest) => {
          let guestHasAddon = !!find(guest.addons, (filterAddon) => {
            return filterAddon.id === addon.id;
          });

          if (guestHasAddon) {
            addons[addon.id]++;
          }
        });

        if (addon.extras) {
          addon.extras.forEach((addonExtra) => {
            addonExtras[addonExtra.id] = 0;

            this.guests.forEach((guest) => {
              let guestHasAddonExtra = !!find(guest.addons, (filterAddon) => {
                return !!find(filterAddon.extras, (filterAddonExtra) => {
                  return filterAddonExtra.id === addonExtra.id;
                });
              });

              if (guestHasAddonExtra) {
                addonExtras[addonExtra.id]++;
              }
            });
          });
        }
      });

      this.booking.data.extras.forEach((extra) => {
        extras[extra.id] = 0;

        this.guests.forEach((guest) => {
          let guestHasExtra = !!find(guest.extras, (filterExtra) => {
            return filterExtra.id === extra.id;
          });

          if (guestHasExtra) {
            extras[extra.id]++;
          }
        });
      });

      this.assignedAddons = addons;
      this.assignedExtras = extras;
      this.assignedAddonExtras = addonExtras;
    },

    onUpdateUnfilledFields(data) {
      let updatedUnfilledFields = this.unfilledFields;
      updatedUnfilledFields = updatedUnfilledFields.filter((item) => item.id !== data.id);
      updatedUnfilledFields = [...updatedUnfilledFields, ...[data]];
      this.unfilledFields = updatedUnfilledFields;

      let total = this.unfilledFields.reduce((acc, item) => {
        return acc + item.quantity;
      }, 0);

      this.$store.commit('details/SET_UNFILLED_FIELDS', total);
      this.countIncompleteGuests();
    },

    onSubmit() {
      this.submitting = true;
      this.resetErrors();

      axios
        .post(Route('json.bookings.update-guests', { booking: this.booking.uuid }), {
          guests: this.unlockedGuests,
        })
        .then((response) => {
          if (response.status !== 200) {
            throw 'Network error.';
          }
          this.guests = response.data.guests;
        })
        .catch(this.handleXHRErrors)
        .then(() => {
          this.submitting = false;
          if (this.allComplete) {
            this.$store.commit('details/SET_UNFILLED_FIELDS', 0);
            this.countIncompleteGuests();
          }
        });
    },

    countIncompleteGuests() {
      let num = this.guests.filter((guest) => {
        return !guest.locked;
      }).length;

      this.$store.commit('details/SET_INCOMPLETE_GUESTS', num);
    },

    getGuestErrors(index) {
      if (typeof this.guestErrors[index] === 'undefined') {
        return {};
      }

      return this.guestErrors[index];
    },
  },

  watch: {
    errors(new_errors) {
      let errors = new Array(this.guests.length);

      forOwn(this.errors, (field_errors, field_name) => {
        let parts = field_name.split('.');
        if (parts[0] === 'guests') {
          if (typeof errors[parts[1]] === 'undefined') {
            errors[parts[1]] = {};
          }

          errors[parts[1]][parts[2]] = field_errors;
        }
      });

      this.guestErrors = errors;
    },
  },
};
</script>
